import { LibCombobox, useComboboxAnchor } from '@round/creatorbase-ui';
import { GenreItem, useSpotifyGenreCombobox } from './useSpotifyGenreCombobox';
import { useMemo, useState } from 'react';
import styles from './SpotifyGenreFilter.module.css';

type Props = {
    value: string[];
    onChange: (genres: string[]) => void;
};

export function SpotifyGenreFilter({ value, onChange }: Props) {
    const { items, isFetching, isError } = useSpotifyGenreCombobox();

    const anchor = useComboboxAnchor();
    const [inputValue, setInputValue] = useState('');

    const selectedItems = useMemo<GenreItem[]>(
        () => value.map((v) => items.find((i) => i.value === v) ?? { value: v, label: v }),
        [value, items]
    );

    const noItemsFound = isError ? 'Failed to load genres' : isFetching ? 'Loading...' : 'No genres found';

    const handleSelect = (selected: GenreItem[] | null) => {
        onChange((selected ?? []).map((g) => g.value));
        setInputValue('');
    };

    return (
        <div className={styles.container}>
            <LibCombobox
                multiple
                value={selectedItems}
                inputValue={inputValue}
                onInputValueChange={setInputValue}
                items={items}
                isItemEqualToValue={(a, b) => a?.value === b?.value}
                onValueChange={handleSelect}
            >
                <div ref={anchor}>
                    <LibCombobox.Input placeholder="Search genres..." showTrigger={false} />
                </div>
                <LibCombobox.Content anchor={anchor}>
                    <LibCombobox.Empty>{noItemsFound}</LibCombobox.Empty>
                    <LibCombobox.List>
                        {(item: GenreItem) => (
                            <LibCombobox.Item key={item.value} value={item}>
                                {item.label}
                            </LibCombobox.Item>
                        )}
                    </LibCombobox.List>
                </LibCombobox.Content>

                <LibCombobox.Value>
                    {(currentValue: GenreItem[] | null) =>
                        currentValue && currentValue.length > 0 ? (
                            <LibCombobox.Chips className={styles.selectedChips}>
                                {currentValue.map((item) => (
                                    <LibCombobox.Chip key={item.value}>{item.label}</LibCombobox.Chip>
                                ))}
                            </LibCombobox.Chips>
                        ) : null
                    }
                </LibCombobox.Value>
            </LibCombobox>
        </div>
    );
}
